// utils/changeCard/compose.js
//
// CHANGE CARD — WHAT A TARGET ELEMENT MAY BE FILLED FROM (KPR-701, KPE-028).
// A person moving items onto another card fills each element of the new card
// from one FILL. This file is the shape of a fill and the vocabulary a formula
// in one may read, for the composer (keepr-web, KPR-708) and the server
// (utils/changeCard/expressions.js, which checks a fill against the real cards
// and works it out item by item). One file, so the composer never offers what
// the server refuses.
//
// THE FILLS
//
//   { kind: 'element', element, mapping }   one source element as it is: the
//       change card table and conversions (compat.js, convert.js), with the
//       unit, currency or choice map chosen once (`mapping`, optional).
//   { kind: 'static', value }                a fixed value of any type, shaped
//       as the item write path stores it (quick add's Fixed rows).
//   { kind: 'formula', formula, blanks }     one expression in the formula
//       language (utils/formula/formula.js), on any target type (quick add's
//       Calculated rows). `blanks` is 'empty' (the default: a blank in stays
//       blank out, never an invented zero) or 'zero'.
//   { kind: 'parts', parts, blanks }         a TEXT target only (a short, long
//       or rich text): parts joined in order, with nothing put between them.
//       A part is { text } (words as typed), { element } (a source element
//       written as a person reads it: a choice's label, an item lookup's
//       title, a person's name) or { formula } (an expression, its result
//       written as text the same way).
//
// WHAT A FORMULA MAY READ (pathOf)
//
//   {{el}}                 an element of the item being moved (the source)
//   {{title}} {{createdAt}} {{updatedAt}}   the item's own title and times
//   {{a.b}} … {{a.b.c.d}}  a dot-walk through item lookups: at most MAX_HOPS
//                          lookups, the same cap as filters (KQL's
//                          MAX_PATH_HOPS); the last name is an element of the
//                          item reached, or its title, createdAt or updatedAt.
//                          A formula is parsed with { maxHops: MAX_HOPS }
//                          (formula.js parse): every other surface keeps the
//                          parser's default of one hop
//   {{user.name}} {{user.email}} {{user.id}}   the person changing the card
//   {{collection.name}} {{collection.id}}      the collection (a card change
//                          stays in it, so it is the target's collection too)
//   today(), now()         the formula language's own: today() is the UTC
//                          date, as in every calculated element
//
// An element of the source card named `user`, `collection`, `title`,
// `createdAt` or `updatedAt` wins over the root of that name (quick add's
// rule). Whether a walk can be followed — each name before the last an item
// lookup, the cards it reaches portable and readable, nothing hidden from the
// person — is the server's question; this file counts the hops.
//
// DEPENDENCY-FREE AND PLATFORM-FREE ON PURPOSE. Vendored byte-identical into
// keepr-web (`src/utils/changeCard/`) with `compose.vectors.json`, which both
// repos' suites drive; `test/vendoredParity.test.js` compares the two mains.
// Nothing here may require() anything, and there is NO SPREAD SYNTAX (Babel
// would inject a helper import and turn this CommonJS file into an ES module
// with no default export in the web build). The two formula limits below are
// formula.js's own, written out because nothing may be required; both suites
// assert they still agree.

'use strict';

// ---------------------------------------------------------------- limits

/** Parts one text target may join. */
var MAX_PARTS = 50;
/** Lookups a dot-walk may follow: KQL's MAX_PATH_HOPS (utils/kql/kql.js). */
var MAX_HOPS = 3;
/** formula.js MAX_FORMULA_LENGTH: one formula, or every formula part of one target together. */
var MAX_FORMULA_LENGTH = 2000;
/** formula.js MAX_REFS: references in one formula, or in one target's parts together (an element part counts as one). */
var MAX_REFS = 50;

var KINDS = ['element', 'static', 'formula', 'parts'];
var BLANKS = ['empty', 'zero'];
/** The targets that take parts: the text kinds. An untyped element reads as a short text. */
var TEXT_TYPES = ['text-small', 'text-large', 'rich-text'];

/** The roots a reference may start from besides the item's own elements, with the words a composer shows. */
var ROOTS = {
	user: [
		{ field: 'name', words: 'the name of the person changing the card' },
		{ field: 'email', words: 'the email address of the person changing the card' },
		{ field: 'id', words: 'the account of the person changing the card' },
	],
	collection: [
		{ field: 'name', words: 'the name of the collection' },
		{ field: 'id', words: 'the id of the collection' },
	],
};
/** The item's own values a formula may read by name. */
var SOURCE_TERMINALS = ['title', 'createdAt', 'updatedAt'];
/** What the last name of a dot-walk may be besides an element of the item reached. */
var WALK_TERMINALS = ['title', 'createdAt', 'updatedAt'];

// ---------------------------------------------------------------- helpers

function hasOwn(obj, key) {
	return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

function isPlainObject(v) {
	return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isBlank(v) {
	return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

function dataTypeOf(el) {
	var type = el && el.dataType;
	return type === undefined || type === null || type === '' ? 'text-small' : String(type);
}

/** The person's word for an element, in quotes: its label, else its name (compat.js's rule). */
function quoted(el) {
	var label = el && el.label;
	var text = label && typeof label === 'object' ? label.singular : label;
	if (typeof text !== 'string' || !text.trim()) text = el && el.name;
	return '"' + (typeof text === 'string' && text.trim() ? text.trim() : 'this element') + '"';
}

/** A name set from an array, a Set or anything with forEach; own-key lookups only. */
function toSet(names) {
	var out = {};
	if (!names) return out;
	var list = [];
	if (Array.isArray(names)) list = names;
	else if (typeof names.forEach === 'function') names.forEach(function (name) { list.push(name); });
	list.forEach(function (name) {
		if (typeof name === 'string' && name) out['$' + name] = true;
	});
	return out;
}

function inSet(set, name) {
	return hasOwn(set, '$' + name);
}

/** Does this target element take parts (any text kind)? */
function takesParts(target) {
	return TEXT_TYPES.indexOf(dataTypeOf(target)) !== -1;
}

function problem(target, part, message) {
	var where = quoted(target) + (part === null || part === undefined ? '' : ', part ' + (part + 1));
	return { problem: { code: 'fill', part: part === undefined ? null : part, message: where + ': ' + message } };
}

function checkMapping(raw) {
	if (raw === undefined || raw === null) return { mapping: {} };
	if (!isPlainObject(raw)) return { error: 'the mapping must name a unit, a currency, a choice map or a unit map.' };
	var out = {};
	if (raw.unit !== undefined && raw.unit !== null && raw.unit !== '') {
		if (typeof raw.unit !== 'string') return { error: 'the unit must be written as text.' };
		out.unit = raw.unit;
	}
	if (raw.currency !== undefined && raw.currency !== null && raw.currency !== '') {
		if (typeof raw.currency !== 'string') return { error: 'the currency must be written as text.' };
		out.currency = raw.currency;
	}
	if (raw.choices !== undefined && raw.choices !== null) {
		if (!isPlainObject(raw.choices)) return { error: 'the choice map must name a target choice for each source choice.' };
		var choices = {};
		var keys = Object.keys(raw.choices);
		for (var i = 0; i < keys.length; i++) {
			var to = raw.choices[keys[i]];
			if (to !== null && typeof to !== 'string') return { error: 'the choice map must name each target choice as text, or leave it empty.' };
			// defineProperty, not `choices[key] = to`: a choice valued
			// "__proto__" would otherwise be lost (compat.js setOwn's rule).
			Object.defineProperty(choices, keys[i], { value: to === null ? '' : to, enumerable: true, writable: true, configurable: true });
		}
		out.choices = choices;
	}
	// A unit map (KPR-1109): each of the source's own units a target lacks
	// goes to a unit the target allows, or is left empty ('').
	if (raw.units !== undefined && raw.units !== null) {
		if (!isPlainObject(raw.units)) return { error: 'the unit map must name a target unit for each source unit.' };
		var units = {};
		var unitKeys = Object.keys(raw.units);
		for (var u = 0; u < unitKeys.length; u++) {
			var into = raw.units[unitKeys[u]];
			if (into !== null && typeof into !== 'string') return { error: 'the unit map must name each target unit as text, or leave it empty.' };
			Object.defineProperty(units, unitKeys[u], { value: into === null ? '' : into, enumerable: true, writable: true, configurable: true });
		}
		out.units = units;
	}
	return { mapping: out };
}

function checkBlanks(raw) {
	if (raw === undefined || raw === null) return 'empty';
	return BLANKS.indexOf(raw) === -1 ? null : raw;
}

// ---------------------------------------------------------------- checkFill

/**
 * checkFill(fill, target) -> { fill } | { problem: { code: 'fill', part, message } }
 *
 * The SHAPE of one target element's fill, before anything is read: the kind,
 * its fields, the part limits and the formula length. `fill` comes back in
 * its stored spelling (unknown keys dropped, `blanks` and `mapping` settled).
 * Whether the element, the value or the formula fits the real cards is the
 * server's (utils/changeCard/expressions.js). The message names the target
 * element and, for a part, its number.
 */
function checkFill(fill, target) {
	if (!isPlainObject(fill) || KINDS.indexOf(fill.kind) === -1) {
		return problem(target, null, 'fill it from an element, a fixed value, a formula or, for a text, parts joined in order.');
	}
	if (fill.kind === 'element') {
		if (typeof fill.element !== 'string' || !fill.element) return problem(target, null, 'name the source element to fill it from.');
		var mapped = checkMapping(fill.mapping);
		if (mapped.error) return problem(target, null, mapped.error);
		return { fill: { kind: 'element', element: fill.element, mapping: mapped.mapping } };
	}
	if (fill.kind === 'static') {
		if (isBlank(fill.value)) return problem(target, null, 'a fixed value needs a value; leave the element out to keep it empty.');
		return { fill: { kind: 'static', value: fill.value } };
	}
	var blanks = checkBlanks(fill.blanks);
	if (blanks === null) return problem(target, null, 'blanks must be "empty" or "zero".');
	if (fill.kind === 'formula') {
		if (typeof fill.formula !== 'string' || !fill.formula.trim()) return problem(target, null, 'write the formula.');
		if (fill.formula.length > MAX_FORMULA_LENGTH) return problem(target, null, 'the formula is too long (limit ' + MAX_FORMULA_LENGTH + ' characters).');
		return { fill: { kind: 'formula', formula: fill.formula, blanks: blanks } };
	}
	// parts
	if (!takesParts(target)) {
		return problem(target, null, 'only a text joins parts; fill ' + quoted(target) + ' from one element, one fixed value or one formula.');
	}
	if (!Array.isArray(fill.parts) || !fill.parts.length) return problem(target, null, 'add at least one part.');
	if (fill.parts.length > MAX_PARTS) return problem(target, null, 'a text joins at most ' + MAX_PARTS + ' parts.');
	var parts = [];
	var formulaLength = 0;
	for (var i = 0; i < fill.parts.length; i++) {
		var part = fill.parts[i];
		var keys = isPlainObject(part) ? ['text', 'element', 'formula'].filter(function (k) { return hasOwn(part, k); }) : [];
		if (keys.length !== 1) return problem(target, i, 'a part is free text, a source element or a formula: exactly one of them.');
		var key = keys[0];
		var value = part[key];
		if (typeof value !== 'string' || (key === 'formula' ? !value.trim() : !value)) {
			return problem(target, i, key === 'text' ? 'free text must be text, and not empty.' : (key === 'element' ? 'name the source element.' : 'write the formula.'));
		}
		if (key === 'formula') formulaLength += value.length;
		var out = {};
		out[key] = value;
		parts.push(out);
	}
	if (formulaLength > MAX_FORMULA_LENGTH) {
		return problem(target, null, 'its formulas are too long together (limit ' + MAX_FORMULA_LENGTH + ' characters across every part).');
	}
	return { fill: { kind: 'parts', parts: parts, blanks: blanks } };
}

// ---------------------------------------------------------------- pathOf

/**
 * The sentence for a reference that names nothing the person may read: ONE
 * sentence whether the name is made up, hidden from them, or a walk that
 * cannot be followed, so the refusal never says which.
 */
function missing(segments) {
	return segments.length > 1
		? '{{' + segments.join('.') + '}} does not exist: every name before the last must be an item lookup, and the last an element of the item it points at.'
		: '{{' + segments[0] + '}} does not exist on this card.';
}

/**
 * pathOf(segments, sourceNames) ->
 *   { root: 'element' | 'walk' | 'terminal' | 'user' | 'collection', hops, field? }
 *   | { problem: { code: 'missing' | 'hops', message } }
 *
 * Which root a reference (its dot-separated names, as the formula parser
 * splits them) starts from, and how many lookups it follows. `sourceNames`
 * are the source card's elements the person may read (an Array or a Set);
 * a name outside them is read as made up.
 */
function pathOf(segments, sourceNames) {
	if (!Array.isArray(segments) || !segments.length || segments.some(function (s) { return typeof s !== 'string' || !s; })) {
		return { problem: { code: 'missing', message: 'A reference names an element: {{name}}.' } };
	}
	var names = toSet(sourceNames);
	var head = segments[0];
	if (inSet(names, head)) {
		if (segments.length === 1) return { root: 'element', hops: 0 };
		var hops = segments.length - 1;
		if (hops > MAX_HOPS) {
			// Word for word what the formula parser says with this cap
			// (formula.js parse, `maxHops`), so whichever answers first, a
			// person reads one sentence.
			return { problem: { code: 'hops', message: '"{{' + segments.join('.') + '}}" walks too far — ' + MAX_HOPS + ' lookup hops are the limit.' } };
		}
		return { root: 'walk', hops: hops };
	}
	if (segments.length === 1 && SOURCE_TERMINALS.indexOf(head) !== -1) return { root: 'terminal', hops: 0 };
	if (hasOwn(ROOTS, head)) {
		var field = segments.length === 2 ? segments[1] : null;
		var known = field !== null && ROOTS[head].some(function (f) { return f.field === field; });
		if (!known) {
			var fields = ROOTS[head].map(function (f) { return '{{' + head + '.' + f.field + '}}'; });
			return { problem: { code: 'missing', message: '{{' + segments.join('.') + '}} does not exist: ' + (head === 'user' ? 'the person changing the card' : 'the collection') + ' gives ' + fields.slice(0, -1).join(', ') + ' and ' + fields[fields.length - 1] + '.' } };
		}
		return { root: head, hops: 0, field: field };
	}
	return { problem: { code: 'missing', message: missing(segments) } };
}

/**
 * The references a composer may offer besides the source card's elements, in
 * order, each with its words: { path, words }. The walks depend on the cards
 * and are the composer's to list from the schema, within MAX_HOPS.
 */
function roots() {
	var out = [
		{ path: 'title', words: 'the title of the item' },
		{ path: 'createdAt', words: 'when the item was created' },
		{ path: 'updatedAt', words: 'when the item was last changed' },
	];
	Object.keys(ROOTS).forEach(function (root) {
		ROOTS[root].forEach(function (f) { out.push({ path: root + '.' + f.field, words: f.words }); });
	});
	return out;
}

module.exports = {
	MAX_PARTS: MAX_PARTS,
	MAX_HOPS: MAX_HOPS,
	MAX_FORMULA_LENGTH: MAX_FORMULA_LENGTH,
	MAX_REFS: MAX_REFS,
	KINDS: KINDS,
	BLANKS: BLANKS,
	TEXT_TYPES: TEXT_TYPES,
	ROOTS: ROOTS,
	SOURCE_TERMINALS: SOURCE_TERMINALS,
	WALK_TERMINALS: WALK_TERMINALS,
	takesParts: takesParts,
	checkFill: checkFill,
	pathOf: pathOf,
	missing: missing,
	roots: roots,
	quoted: quoted,
};
